import { useState } from "react";
import gql from "graphql-tag";
import { useApolloClient, useQuery, QueryHookOptions, QueryOptions } from "@apollo/client";
import { replacePlusFromURLPath } from "@ugg/shared/utils/url-query-params";
import { INTERNAL_CURRENT_SEASON } from "@ugg/shared/constants/seasons";
import { MatchLP } from "@ugg/shared/components/SummonerProfiles/overview/match-history/match-history-helpers";

type MatchSummaryParams = Partial<{
  queueType: number[];
  duoName: string;
  role: number[];
  season: number;
  championId: number;
}>;

export function getParamsForFetchMatchSummaryQuery(region: string, summonerName: string, queryParams: MatchSummaryParams = {}) {
  return {
    regionId: region,
    summonerName: replacePlusFromURLPath(summonerName.toLowerCase()),
    queueType: queryParams.queueType || [],
    duoName: queryParams.duoName || "",
    role: queryParams.role || [],
    seasonIds: !!queryParams.season ? [queryParams.season] : [INTERNAL_CURRENT_SEASON, INTERNAL_CURRENT_SEASON - 1],
    championId: !queryParams.championId || isNaN(queryParams.championId) ? [] : [queryParams.championId],
  };
}

export function useFetchSummonerMatchSummaries(
  regionId: string,
  summonerName: string,
  queryParams: MatchSummaryParams,
  queryOptions: QueryHookOptions = {},
) {
  const params = getParamsForFetchMatchSummaryQuery(regionId, summonerName, queryParams || {});

  return useQuery<PlayerMatchSummariesData>(FETCH_MATCH_SUMMARIES, {
    ...queryOptions,
    variables: params,
  });
}

export function useFetchSummonerMatchSummariesLazily(
  regionId: string,
  summonerName: string,
  queryParams: MatchSummaryParams,
  queryOptions?: QueryOptions,
) {
  const client = useApolloClient();
  const params = getParamsForFetchMatchSummaryQuery(regionId, summonerName, queryParams || {});

  const [fetchState, setFetchState] = useState<{
    loading: boolean;
    data: PlayerMatchSummariesData["fetchPlayerMatchSummaries"] | null;
    error: Error | null;
  }>({ loading: false, data: null, error: null });

  const getSummonerMatchSummaries = (page: number) => {
    setFetchState({ loading: true, data: null, error: null });
    client
      .query<PlayerMatchSummariesData>({
        ...(queryOptions || {}),
        query: FETCH_MATCH_SUMMARIES,
        variables: { ...params, page: page },
      })
      .then((response) => {
        setFetchState({ loading: false, data: response.data.fetchPlayerMatchSummaries, error: null });
      })
      .catch((error) => {
        setFetchState({ loading: false, data: null, error: error });
      });
  };

  return [getSummonerMatchSummaries, fetchState] as const;
}

export interface TeamData {
  championId: number;
  summonerName: string;
  teamId: number;
  role: number;
  hardCarry: number;
  teamplay: number;
  placement: number;
  playerSubteamId: number;
}
export interface MatchSummary {
  assists: number;
  augments: number[];
  championId: number;
  cs: number;
  damage: number;
  damageTaken: number;
  deaths: number;
  gold: number;
  items: number[];
  jungleCs: number;
  killParticipation: number;
  kills: number;
  level: number;
  matchCreationTime: number;
  matchDuration: number;
  matchId: number;
  maximumKillStreak: number;
  primaryStyle: number;
  queueType: string;
  regionId: string;
  role: number;
  runes: number[];
  subStyle: number;
  summonerName: string;
  summonerSpells: number[];
  psHardCarry: number;
  psTeamPlay: number;
  lpInfo: MatchLP | null;
  teamA: TeamData[];
  teamB: TeamData[];
  version: string;
  visionScore: number;
  win: boolean;
}
export interface PlayerMatchSummariesData {
  fetchPlayerMatchSummaries: {
    finishedMatchSummaries: boolean;
    matchSummaries: MatchSummary[];
    totalNumMatches: number;
  };
}

const TeamData = `
  championId
  summonerName
  teamId
  role
  hardCarry
  teamplay
  placement
  playerSubteamId
`;

export const MatchSummaryInfo = `
  assists
  augments
  championId
  cs
  damage
  deaths
  gold
  items
  jungleCs
  killParticipation
  kills
  level
  matchCreationTime
  matchDuration
  matchId
  maximumKillStreak
  primaryStyle
  queueType
  regionId
  role
  runes
  subStyle
  summonerName
  summonerSpells
  psHardCarry
  psTeamPlay
  lpInfo {
    lp
    placement
    promoProgress
    promoTarget
    promotedTo {
      tier
      rank
    }
  }
  teamA {
    ${TeamData}
  }
  teamB {
    ${TeamData}
  }
  version
  visionScore
  win
`;

const FETCH_MATCH_SUMMARIES = gql`
  query FetchMatchSummaries(
    $championId: [Int],
    $page: Int,
    $queueType: [Int],
    $duoName: String,
    $regionId: String!,
    $role:  [Int],
    $seasonIds: [Int]!,
    $summonerName: String!
  ) {
    fetchPlayerMatchSummaries(
      championId: $championId,
      page: $page,
      queueType: $queueType,
      duoName: $duoName,
      regionId: $regionId,
      role: $role,
      seasonIds: $seasonIds,
      summonerName: $summonerName
    ) {
      finishedMatchSummaries
      totalNumMatches
      matchSummaries {
        ${MatchSummaryInfo}
      }
    }
  }
`;
